import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { usePermissionsStore } from '@/stores/permissions';
import { logout } from '@/auth/oidc';
import { searchProjects } from '@/api/planning';
import type { ProjectSearchResult } from '@/api/planning';

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  return words
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export function Header() {
  const user = useAuthStore((s) => s.user);
  const initials = user ? getInitials(user.name) : '??';
  // A Bright Analytics person sees only their key page; the project search would 403.
  const noSearch = usePermissionsStore((s) => s.canView('bright_api'));

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ProjectSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Debounced search (300ms, min 3 chars)
  useEffect(() => {
    if (searchQuery.length < 3) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    setSearchLoading(true);
    setShowResults(true);
    const timer = setTimeout(async () => {
      try {
        const { results } = await searchProjects(searchQuery);
        setSearchResults(results);
      } catch (err) {
        console.error('Project search failed:', err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Click-outside to close
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleResultClick = useCallback((result: ProjectSearchResult) => {
    navigate(`/project/${result.id}`);
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  }, [navigate]);

  return (
    <div className="bg-gradient-to-r from-td-blue via-td-blue-dark to-td-magenta/80 relative">
      {/* Geometric pattern overlay */}
      <div
        className="absolute inset-0 opacity-10 overflow-hidden"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'><polygon points='30,0 60,30 30,60 0,30' fill='white' fill-opacity='0.3'/></svg>\")",
          backgroundSize: '60px 60px',
        }}
      />
      <div className="relative flex items-center justify-between px-4 py-3 lg:px-8 lg:py-4">
        <img src={`${import.meta.env.BASE_URL}logo-white.png`} alt="Thermoduct" className="h-8 lg:h-12" />
        <div className="flex items-center gap-4">
          <div ref={searchRef} className={`relative hidden ${noSearch ? '' : 'lg:block'}`}>
            <svg
              className="absolute left-3 top-2.5 w-4 h-4 text-white/50"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <label htmlFor="header-project-search" className="sr-only">Project zoeken</label>
            <input
              id="header-project-search"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setShowResults(false);
                  setSearchQuery('');
                  (e.target as HTMLInputElement).blur();
                }
              }}
              placeholder="Zoek project..."
              className="w-72 pl-9 pr-4 py-2 text-sm bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:bg-white/20 focus:border-white/40 transition"
            />

            {/* Search results dropdown */}
            {showResults && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-80 overflow-y-auto">
                {searchLoading && (
                  <p className="text-sm text-td-muted px-4 py-3">Zoeken...</p>
                )}
                {!searchLoading && searchResults.length === 0 && (
                  <p className="text-sm text-td-muted px-4 py-3">Geen projecten gevonden</p>
                )}
                {!searchLoading && searchResults.map((result) => (
                  <button
                    key={result.id}
                    type="button"
                    onClick={() => handleResultClick(result)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors cursor-pointer"
                  >
                    <p className="text-sm font-semibold text-td-navy truncate">
                      {result.projectName || result.name}
                      {result.sale_order_name && (
                        <span className="ml-2 text-xs font-normal text-td-muted">
                          {result.sale_order_name}
                        </span>
                      )}
                    </p>
                    {result.address && (
                      <p className="text-xs text-td-muted truncate mt-0.5">{result.address}</p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={import.meta.env.VITE_SKIP_AUTH !== 'true' ? () => logout() : undefined}
            title={import.meta.env.VITE_SKIP_AUTH !== 'true' ? 'Uitloggen' : user?.name ?? ''}
            className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold text-white hover:bg-white/30 transition cursor-pointer"
          >
            {initials}
          </button>
        </div>
      </div>
    </div>
  );
}
